import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useGoogleLogin } from "@react-oauth/google";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  BadgePercent,
  Briefcase,
  Building2,
  FileBadge2,
  FileText,
  Globe,
  KeyRound,
  LogIn,
  Mail,
  MessageCircle,
  Phone,
  ShieldCheck,
  User,
  VenusAndMars,
} from "lucide-react";
import { countryOptions, fileRequiredTest, heroImage } from "./constants/register";
import type { RegistrationValues } from "./types/registration";
import {
  accentText,
  cardClass,
  fileInputClass,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "./styles";
import { AccountTypeSelector } from "./components/AccountTypeSelector";
import { PasswordChecklist } from "./components/PasswordChecklist";
import { Field } from "./components/Field";
import { CountryPhoneField } from "./components/CountryPhoneField";
import { GoogleRegisterSection } from "./components/GoogleRegisterSection";

export function Register({
  onSubmitRegistration,
  onGoogleIndividualLogin,
  googleEnabled,
}: {
  onSubmitRegistration: (data: RegistrationValues) => Promise<void>;
  onGoogleIndividualLogin: (data: { accessToken: string }) => Promise<void>;
  googleEnabled: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(0);

  const schema = useMemo(
    () =>
      yup.object({
        accountType: yup.mixed<"individual" | "company">().oneOf(["individual", "company"]).required(),
        fullName: yup.string().required(t("required")),
        email: yup.string().email(t("invalidEmail")).required(t("required")),
        password: yup
          .string()
          .required(t("required"))
          .matches(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/, t("passwordPolicy")),
        gender: yup.string().when("accountType", {
          is: "individual",
          then: (s) => s.oneOf(["male", "female", "prefer_not"], t("required")).required(t("required")),
          otherwise: (s) => s.optional(),
        }),
        individualCountryCode: yup.string().when("accountType", {
          is: "individual",
          then: (s) => s.required(t("required")),
          otherwise: (s) => s.optional(),
        }),
        individualPhone: yup.string().when("accountType", {
          is: "individual",
          then: (s) => s.matches(/^\d{6,14}$/, t("invalidPhone")).required(t("required")),
          otherwise: (s) => s.optional(),
        }),
        companyName: yup.string().when("accountType", {
          is: "company",
          then: (s) => s.required(t("required")),
          otherwise: (s) => s.optional(),
        }),
        companyUrl: yup.string().when("accountType", {
          is: "company",
          then: (s) => s.url(t("invalidUrl")).required(t("required")),
          otherwise: (s) => s.optional(),
        }),
        whatsappCountryCode: yup.string().when("accountType", {
          is: "company",
          then: (s) => s.required(t("required")),
          otherwise: (s) => s.optional(),
        }),
        whatsapp: yup.string().when("accountType", {
          is: "company",
          then: (s) => s.matches(/^\d{6,14}$/, t("invalidPhone")).required(t("required")),
          otherwise: (s) => s.optional(),
        }),
        responsibleName: yup.string().when("accountType", {
          is: "company",
          then: (s) => s.required(t("required")),
          otherwise: (s) => s.optional(),
        }),
        responsibleCountryCode: yup.string().when("accountType", {
          is: "company",
          then: (s) => s.required(t("required")),
          otherwise: (s) => s.optional(),
        }),
        responsiblePhone: yup.string().when("accountType", {
          is: "company",
          then: (s) => s.matches(/^\d{6,14}$/, t("invalidPhone")).required(t("required")),
          otherwise: (s) => s.optional(),
        }),
        responsibleTitle: yup.string().when("accountType", {
          is: "company",
          then: (s) => s.required(t("required")),
          otherwise: (s) => s.optional(),
        }),
        responsibleInRecord: yup.string().when("accountType", {
          is: "company",
          then: (s) => s.oneOf(["yes", "no"], t("required")).required(t("required")),
          otherwise: (s) => s.optional(),
        }),
        incCert: yup.mixed().nullable().when("accountType", {
          is: "company",
          then: (s) => s.test("required", t("required"), fileRequiredTest),
          otherwise: (s) => s.optional(),
        }),
        licenseCert: yup.mixed().nullable().when("accountType", {
          is: "company",
          then: (s) => s.test("required", t("required"), fileRequiredTest),
          otherwise: (s) => s.optional(),
        }),
        taxCard: yup.mixed().nullable().optional(),
        officialCompanyEmail: yup.string().when("accountType", {
          is: "company",
          then: (s) => s.email(t("invalidEmail")).required(t("required")),
          otherwise: (s) => s.optional(),
        }),
      }),
    [t]
  );

  const {
    register,
    watch,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm<RegistrationValues>({
    resolver: yupResolver(schema) as any,
    mode: "onTouched",
    defaultValues: {
      accountType: "individual", fullName: "", email: "", password: "", gender: "",
      individualCountryCode: "+20", individualPhone: "",
      companyName: "", companyUrl: "", whatsappCountryCode: "+20", whatsapp: "",
      responsibleName: "", responsibleCountryCode: "+20", responsiblePhone: "",
      responsibleTitle: "", responsibleInRecord: "",
      incCert: null, licenseCert: null, taxCard: null, officialCompanyEmail: "",
    },
  });

  const accountType = watch("accountType");
  const isCompany = accountType === "company";
  const totalSteps = isCompany ? 4 : 2;

  useEffect(() => { if (!isCompany) setStep(0); }, [isCompany]);

  const companyStepFields: (keyof RegistrationValues)[][] = [
    ["fullName", "email", "password", "accountType"],
    ["companyName", "companyUrl", "whatsappCountryCode", "whatsapp", "officialCompanyEmail"],
    ["responsibleName", "responsibleCountryCode", "responsiblePhone", "responsibleTitle", "responsibleInRecord"],
    ["incCert", "licenseCert", "taxCard"],
  ];
  const individualStepFields: (keyof RegistrationValues)[][] = [
    ["fullName", "email", "password", "accountType"],
    ["gender", "individualCountryCode", "individualPhone"],
  ];
  const stepFields = isCompany ? companyStepFields : individualStepFields;
  const labels = isCompany
    ? [t("stepBasic"), t("stepCompany"), t("stepResponsible"), t("stepDocs")]
    : [t("stepIndividualIdentity"), t("stepIndividualProfile")];

  const passwordValue = watch("password") ?? "";
  const passwordChecks = [
    { key: "length", label: t("passwordRuleLength"), valid: passwordValue.length >= 8 },
    { key: "letter", label: t("passwordRuleLetter"), valid: /[A-Za-z]/.test(passwordValue) },
    { key: "number", label: t("passwordRuleNumber"), valid: /\d/.test(passwordValue) },
    { key: "symbol", label: t("passwordRuleSymbol"), valid: /[^A-Za-z\d]/.test(passwordValue) },
  ];
  const passedChecks = passwordChecks.filter((item) => item.valid).length;
  const getCountryLabel = (option: (typeof countryOptions)[number]) =>
    `${option.code} - ${i18n.language === "ar" ? option.ar : option.en}`;

  const [googleLoading, setGoogleLoading] = useState(false);

  const googleRegister = useGoogleLogin({
    scope: "openid email profile",
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      try {
        await onGoogleIndividualLogin({ accessToken: tokenResponse.access_token });
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => undefined,
  });

  const nextStep = async () => {
    const valid = await trigger(stepFields[step]);
    if (valid) setStep((current) => Math.min(current + 1, totalSteps - 1));
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      {/* ─── Hero ─── */}
      <section
        className="relative hidden lg:flex lg:flex-col lg:justify-between lg:overflow-hidden lg:p-14"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0,5,16,0.82), rgba(12,55,42,0.55), rgba(0,5,16,0.7)), url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >

        <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.1 }} className="max-w-lg mt-30 text-white">
          <h1 className="text-5xl font-extrabold leading-[1.1] tracking-tight">{t("title")}</h1>
          <p className="mt-5 text-lg leading-relaxed text-white/80">{t("subtitle")}</p>
          <div className="mt-8 flex items-center gap-4">
            <div className="flex -space-x-2">
              {["bg-[var(--color-primary)]", "bg-[var(--color-secondary)]", "bg-[var(--color-tealGreen)]"].map((c, i) => (
                <div key={i} className={`h-8 w-8 rounded-full border-2 border-[var(--color-darkmode)] ${c}`} />
              ))}
            </div>
            <p className="text-sm text-white/70">{t("businessSubtitle")}</p>
          </div>
        </motion.div>

        <div className="flex gap-6 text-xs text-white/50">
          <span>256-bit SSL</span><span>•</span><span>GDPR Compliant</span><span>•</span><span>SOC 2</span>
        </div>
      </section>

      {/* ─── Form ─── */}
      <section className="relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-[var(--color-grey)] via-[var(--color-white)] to-[var(--color-grey)] p-4 dark:from-[var(--color-darkmode)] dark:via-[var(--color-deepSlate)] dark:to-[var(--color-darkmode)] sm:p-10">

        <div className="mt-30 w-1/1">

        <motion.div aria-hidden initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }} className="pointer-events-none absolute -end-32 -top-20 h-96 w-96 rounded-full bg-[var(--color-tealGreen)]/10 blur-[100px] dark:bg-[var(--color-primary)]/8" />
        <motion.div aria-hidden initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2 }} className="pointer-events-none absolute -start-28 bottom-0 h-80 w-80 rounded-full bg-[var(--color-secondary)]/10 blur-[100px] dark:bg-[var(--color-primary)]/6" />

        <div className={cardClass}>
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.6 }} className="mb-6 h-1 w-full origin-left rounded-full bg-gradient-to-r from-[var(--color-tealGreen)] via-[var(--color-secondary)] to-[var(--color-tealGreen)] dark:from-[var(--color-primary)] dark:via-[var(--color-secondary)] dark:to-[var(--color-primary)]" />

          <div className="mb-6 flex items-start justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--color-tealGreen)] dark:text-[var(--color-primary)]">{t("brand")}</p>
              <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-[var(--color-midnight_text)] dark:text-[var(--color-white)]">{t("title")}</h2>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-tealGreen)]/10 px-3 py-1 text-xs font-bold text-[var(--color-tealGreen)] dark:bg-[var(--color-primary)]/15 dark:text-[var(--color-primary)]">
              {`${step + 1}/${totalSteps}`}
            </span>
          </div>

          <div className="mb-6 inline-flex items-center gap-2 text-sm text-[var(--color-charcoalGray)] dark:text-[var(--color-muted)]">
            <LogIn size={15} className={accentText} />
            <span>{t("haveAccount")}</span>
            <Link to="/login" className="font-semibold text-[var(--color-tealGreen)] transition hover:text-[var(--color-secondary)] dark:text-[var(--color-primary)] dark:hover:text-[var(--color-secondary)]">{t("goToLogin")}</Link>
          </div>

          <div className="mb-7">
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--color-muted)]/50 dark:bg-[var(--color-slateGray)]">
              <motion.div className="h-full rounded-full bg-gradient-to-r from-[var(--color-tealGreen)] to-[var(--color-secondary)] dark:from-[var(--color-primary)] dark:to-[var(--color-tealGreen)]" animate={{ width: `${((step + 1) / totalSteps) * 100}%` }} transition={{ type: "spring", stiffness: 140, damping: 22 }} />
            </div>
            <p className="mt-2.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-charcoalGray)] dark:text-[var(--color-muted)]">{labels[step]}</p>
          </div>

          <form onSubmit={handleSubmit((data) => onSubmitRegistration(data as RegistrationValues).catch(() => {}))} className="space-y-5">
            <AccountTypeSelector t={t} accountType={accountType} register={register} />

            <AnimatePresence mode="wait">
              <motion.div key={`${accountType}-${step}-${i18n.language}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }} transition={{ duration: 0.25 }} className="space-y-5">
                {step === 0 && (
                  <>
                    <Field label={t("fullName")} error={errors.fullName?.message} icon={<User size={14} className={accentText} />}><input className={inputClass} {...register("fullName")} /></Field>
                    <Field label={t("email")} error={errors.email?.message} icon={<Mail size={14} className={accentText} />}><input type="email" className={inputClass} {...register("email")} /></Field>
                    <Field label={t("password")} error={errors.password?.message} icon={<KeyRound size={14} className={accentText} />}><input type="password" className={inputClass} {...register("password")} /></Field>
                    {accountType === "individual" && <GoogleRegisterSection t={t} googleEnabled={googleEnabled} googleRegister={() => googleRegister()} loading={googleLoading} />}
                    <PasswordChecklist t={t} passedChecks={passedChecks} passwordChecks={passwordChecks} />
                  </>
                )}

                {!isCompany && step === 1 && (
                  <>
                    <Field label={t("gender")} error={errors.gender?.message} icon={<VenusAndMars size={14} className={accentText} />}>
                      <select className={inputClass} {...register("gender")}>
                        <option value="">--</option><option value="male">{t("male")}</option><option value="female">{t("female")}</option><option value="prefer_not">{t("preferNotToSay")}</option>
                      </select>
                    </Field>
                    <CountryPhoneField label={t("individualPhone")} error={(errors.individualCountryCode?.message || errors.individualPhone?.message) as string | undefined} icon={<Phone size={14} className={accentText} />} codeName="individualCountryCode" phoneName="individualPhone" register={register} placeholder={t("phoneNumberPlaceholder")} getCountryLabel={getCountryLabel} />
                    <div className="rounded-xl border border-[var(--color-border)]/60 bg-gradient-to-br from-[var(--color-grey)]/60 to-[var(--color-white)] p-4 text-sm text-[var(--color-charcoalGray)] dark:border-[var(--color-dark_border)]/60 dark:from-[var(--color-slateGray)]/30 dark:to-[var(--color-deepSlate)]/30 dark:text-[var(--color-muted)]">{t("individualStepNote")}</div>
                  </>
                )}

                {isCompany && step === 1 && (
                  <>
                    <Field label={t("companyName")} error={errors.companyName?.message} icon={<Building2 size={14} className={accentText} />}><input className={inputClass} {...register("companyName")} /></Field>
                    <Field label={t("companyUrl")} error={errors.companyUrl?.message} icon={<Globe size={14} className={accentText} />}><input placeholder="https://" className={inputClass} {...register("companyUrl")} /></Field>
                    <CountryPhoneField label={t("whatsapp")} error={(errors.whatsappCountryCode?.message || errors.whatsapp?.message) as string | undefined} icon={<MessageCircle size={14} className={accentText} />} codeName="whatsappCountryCode" phoneName="whatsapp" register={register} placeholder={t("phoneNumberPlaceholder")} getCountryLabel={getCountryLabel} />
                    <Field label={t("officialCompanyEmail")} error={errors.officialCompanyEmail?.message} icon={<Mail size={14} className={accentText} />}><input type="email" className={inputClass} {...register("officialCompanyEmail")} /></Field>
                  </>
                )}

                {isCompany && step === 2 && (
                  <>
                    <Field label={t("responsibleName")} error={errors.responsibleName?.message} icon={<User size={14} className={accentText} />}><input className={inputClass} {...register("responsibleName")} /></Field>
                    <CountryPhoneField label={t("responsiblePhone")} error={(errors.responsibleCountryCode?.message || errors.responsiblePhone?.message) as string | undefined} icon={<Phone size={14} className={accentText} />} codeName="responsibleCountryCode" phoneName="responsiblePhone" register={register} placeholder={t("phoneNumberPlaceholder")} getCountryLabel={getCountryLabel} />
                    <Field label={t("responsibleTitle")} error={errors.responsibleTitle?.message} icon={<Briefcase size={14} className={accentText} />}><input className={inputClass} {...register("responsibleTitle")} /></Field>
                    <Field label={t("responsibleInRecord")} error={errors.responsibleInRecord?.message} icon={<ShieldCheck size={14} className={accentText} />}>
                      <select className={inputClass} {...register("responsibleInRecord")}>
                        <option value="">--</option><option value="yes">{t("yes")}</option><option value="no">{t("no")}</option>
                      </select>
                    </Field>
                  </>
                )}

                {isCompany && step === 3 && (
                  <>
                    <Field label={t("incCert")} error={errors.incCert?.message as string | undefined} icon={<FileText size={14} className={accentText} />}><input type="file" className={fileInputClass} {...register("incCert")} /></Field>
                    <Field label={t("licenseCert")} error={errors.licenseCert?.message as string | undefined} icon={<FileBadge2 size={14} className={accentText} />}><input type="file" className={fileInputClass} {...register("licenseCert")} /></Field>
                    <Field label={t("taxCard")} error={errors.taxCard?.message as string | undefined} icon={<BadgePercent size={14} className={accentText} />}><input type="file" className={fileInputClass} {...register("taxCard")} /></Field>
                  </>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="flex justify-between gap-3 pt-3">
              {step > 0 ? (
                <button type="button" onClick={() => setStep((current) => Math.max(current - 1, 0))} className={secondaryButtonClass}>{t("back")}</button>
              ) : (
                <span />
              )}
              {step < totalSteps - 1 ? (
                <button type="button" onClick={nextStep} className={primaryButtonClass}>{t("next")}</button>
              ) : (
                <button type="submit" className={primaryButtonClass}>{t("submit")}</button>
              )}
            </div>

            <p className="pt-1 text-center text-[11px] text-[var(--color-charcoalGray)]/70 dark:text-[var(--color-muted)]/70">{t("registrationNote")}</p>
          </form>
        </div>

        </div>
      </section>
    </div>
  );
}
